/**
 * public-tournaments-api.ts - API client for public tournament directory
 *
 * Handles all HTTP requests related to public tournament search and discovery.
 */

export interface PublicTournamentFilters {
  location?: string;
  radius?: number;
  timeControl?: string;
  startDate?: string;
  endDate?: string;
  format?: string;
  isRated?: boolean;
  status?: string;
}

/**
 * Search public tournaments with filters
 * @param filters - Search filters
 * @returns Object containing tournaments array and count
 * @throws Error if request fails
 */
export async function searchPublicTournaments(filters: PublicTournamentFilters = {}): Promise<{
  tournaments: any[];
  count: number;
}> {
  const params = new URLSearchParams();

  if (filters.location) params.append('location', filters.location);
  if (filters.radius) params.append('radius', filters.radius.toString());
  if (filters.timeControl) params.append('timeControl', filters.timeControl);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.format) params.append('format', filters.format);
  if (filters.isRated !== undefined) params.append('isRated', filters.isRated.toString());
  if (filters.status) params.append('status', filters.status);

  const queryString = params.toString();
  const url = `/api/tournaments/public${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch public tournaments');
  }

  return await response.json();
}

/**
 * Publish a tournament to make it visible in public directory
 * @param tournamentId - Tournament ID
 * @returns Success message
 * @throws Error if request fails
 */
export async function publishTournament(tournamentId: string): Promise<{ message: string; tournament: any }> {
  const response = await fetch(`/api/tournaments/${tournamentId}/publish`, {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to publish tournament');
  }

  return await response.json();
}

/**
 * Unpublish a tournament to remove it from public directory
 * @param tournamentId - Tournament ID
 * @returns Success message
 * @throws Error if request fails
 */
export async function unpublishTournament(tournamentId: string): Promise<{ message: string; tournament: any }> {
  const response = await fetch(`/api/tournaments/${tournamentId}/unpublish`, {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unpublish tournament');
  }

  return await response.json();
}
